/**
 * Which routes of the members' app can be reached without a session.
 *
 * Only relevant in the member build. The public build has no session to be
 * without, and this is never asked there.
 *
 * # An allowlist of the ways in, not a list of what is protected
 *
 * The members' app is protected in one place — the root layout, on mount — so
 * the question it asks has to be "may this page be seen signed out?" rather
 * than "is this page private?". A list of private pages is a list somebody has
 * to remember to add to, and the cost of forgetting is a screen served without
 * a session. A list of public ones fails the other way: forget to add a route
 * here and it sends people to the login screen, which is annoying and visible
 * rather than quiet and wrong.
 */

/** The paths of the members' app that a signed-out visitor may see. */
const openPaths = [
  // Where signing out sends people, where a reset link lands, and the only
  // page that can do anything about not having a session.
  '/login',
  // Where checkout returns to, for the same reason it is open itself.
  '/thank-you'
];

/** Path prefixes open to a signed-out visitor, for routes with parameters. */
const openPrefixes = [
  // A checkout link is followed from an email, often on a different device
  // from the one the order was placed on. Requiring a session would make the
  // invoice unpayable by the person who was sent it.
  '/checkout/'
];

/**
 * Whether this path needs somebody signed in.
 *
 * A trailing slash is the same page as no trailing slash, which is worth
 * handling here rather than in the one caller: some links carry it, and a
 * `/login/` that redirected to `/login` would be a redirect loop.
 */
export function needsSession(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  if (openPaths.includes(path)) return false;
  return !openPrefixes.some((prefix) => path.startsWith(prefix));
}

/**
 * The API paths the members' app may call without a token.
 *
 * These are exactly what the signed-out routes above need, and the two lists
 * have to be read together: a page that can be reached without a session is
 * useless if the calls it makes are refused before they leave the browser.
 */
const openEndpointPrefixes = [
  // Signing in, registering, and the two halves of a password reset. `/auth/me`
  // is here too and is harmless: it is only ever called with a token, and
  // without one the API refuses it, which is the right answer rather than a
  // request that should not have been made.
  '/auth/',
  // What `/checkout/:id` polls for the payment status, and the anonymous order
  // it belongs to. Open because the checkout page is open — a link followed
  // from an email, often on another device.
  //
  // Placing an anonymous order also sits under here. Nothing in the members'
  // app can reach that screen, so this opens a door with no corridor to it;
  // narrowing it by method would be precision about a call that cannot happen.
  '/order/'
];

/**
 * Whether calling this API path requires a session in the members' build.
 *
 * # Why the client refuses rather than letting the API refuse
 *
 * A request sent without a token is answered, just not usefully: the public
 * endpoints return masked rows and a 200. So in the members' app an untokened
 * call does not fail loudly — it succeeds quietly with the visitor's data, and
 * the screen draws it as though it were the member's. That is the whole failure
 * this build exists to remove, and it is worth removing at the last point the
 * app still controls: before the request leaves.
 *
 * It also keeps a lapsed session from spraying the API with calls that cannot
 * do anything, while the redirect to `/login` is still in flight.
 */
export function endpointNeedsSession(path: string): boolean {
  return !openEndpointPrefixes.some((prefix) => path.startsWith(prefix));
}
